import { useEffect, useState } from 'react';
import { cloudClient } from '@/integrations/supabase/cloudClient';
import { TIERS, type TierEnum, getTierByEnum } from '@/constants/tiers';

export interface TierCatalogEntry {
  key: TierEnum;
  planId: string;
  displayName: string;
  tagline: string;
  description: string;
  priceMonthly: number;
  priceAddon: number;
  includedFeatures: string[];      // raw feature_keys
  panelCategories: string[];       // installation | rental | touring | studio
  featureLabels: string[];         // human labels for bullets
  panelCategoryLabels: string[];   // human labels for bullets
}

const CATEGORY_LABEL: Record<string, string> = {
  installation: 'Full library of installation panels',
  rental: 'Full library of rental panels',
  touring: 'Full library of touring panels',
  studio: 'Full library of studio panels',
};

// Humanize a feature_key like "touring_power_flow" → "Touring power flow".
const humanize = (key: string): string => {
  const spaced = key.replace(/[_-]+/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

interface PlanRow {
  id: string;
  key: string;
  name: string;
  price_usd_monthly: number;
  price_addon_usd_monthly: number;
  included_features: string[];
  granted_panel_categories: string[];
  active: boolean;
  is_internal: boolean;
}
interface FlagRow { feature_key: string; label: string; }

let cache: { catalog: TierCatalogEntry[]; flags: FlagRow[] } | null = null;
let inflight: Promise<{ catalog: TierCatalogEntry[]; flags: FlagRow[] }> | null = null;

async function load() {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = (async () => {
    const [plansRes, flagsRes] = await Promise.all([
      cloudClient.from('subscription_plans').select('*').eq('active', true).order('sort_order'),
      cloudClient.from('feature_flags').select('feature_key,label'),
    ]);
    const plans = ((plansRes.data ?? []) as unknown as PlanRow[]).filter(p => !p.is_internal);
    const flags = (flagsRes.data ?? []) as FlagRow[];
    const flagMap = new Map(flags.map(f => [f.feature_key, f.label]));

    const catalog: TierCatalogEntry[] = plans
      .map(p => {
        const meta = getTierByEnum(p.key as TierEnum);
        if (!meta) return null;
        const features = p.included_features ?? [];
        const cats = p.granted_panel_categories ?? [];
        return {
          key: p.key as TierEnum,
          planId: p.id,
          displayName: meta.displayName,
          tagline: meta.tagline,
          description: meta.description,
          priceMonthly: Number(p.price_usd_monthly) || 0,
          priceAddon: Number(p.price_addon_usd_monthly) || 0,
          includedFeatures: features,
          panelCategories: cats,
          featureLabels: features.map(f => flagMap.get(f) ?? humanize(f)),
          panelCategoryLabels: cats.map(c => CATEGORY_LABEL[c] ?? c),
        } as TierCatalogEntry;
      })
      .filter((x): x is TierCatalogEntry => x !== null);

    cache = { catalog, flags };
    return cache;
  })();
  try { return await inflight; } finally { inflight = null; }
}

export function useTierCatalog() {
  const [catalog, setCatalog] = useState<TierCatalogEntry[]>(cache?.catalog ?? []);
  const [loading, setLoading] = useState(!cache);
  useEffect(() => {
    let live = true;
    load().then(r => { if (live) { setCatalog(r.catalog); setLoading(false); } })
      .catch(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);
  return { catalog, loading };
}

// Pricing rule mirrors compute_monthly_price() in the DB:
// 1 paid tier = base ($10), each additional paid tier = +addon ($5). Free = $0.
export function computePrice(selected: TierEnum[], catalog: TierCatalogEntry[]): number {
  const paid = catalog.filter(t => selected.includes(t.key) && t.priceMonthly > 0);
  if (paid.length === 0) return 0;
  const base = Math.max(...paid.map(t => t.priceMonthly));
  const addon = paid[0]?.priceAddon ?? 0;
  return base + (paid.length - 1) * addon;
}

export function invalidateTierCatalog() { cache = null; }
